import { useCallback, useEffect } from 'react';
import { Box, Card, CardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  appendAuthHandoffParams,
  isTrustedRedirectUrl,
  readAuthHandoffParams,
} from '@trust_network/shared/auth-handoff';
import { useUserStore, userSelectors } from '../../../entities/user';
import { AuthFeature, AuthMode, AuthOutcomeContext } from './AuthFeature';

interface AuthCardProps {
  defaultReturnTo?: string;
  subtitle?: string;
  allowedModes?: AuthMode[];
  initialMode?: AuthMode;
}

function getSafeReturnTo(
  candidate: string | null | undefined,
  fallback: string
) {
  const normalizedFallback =
    fallback.startsWith('/') && !fallback.startsWith('//') ? fallback : '/';

  if (!candidate) {
    return normalizedFallback;
  }

  return candidate.startsWith('/') && !candidate.startsWith('//')
    ? candidate
    : normalizedFallback;
}

const TRUSTED_HANDOFF_ORIGINS = [import.meta.env.VITE_ADMIN_ORIGIN].filter(
  (origin): origin is string => Boolean(origin)
);

function isKnownAuthMode(value: string | null): value is AuthMode {
  return value === 'login' || value === 'register' || value === 'recovery';
}

const AuthCardFrame = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2),
  },
}));

const AuthSurface = styled(Card)({
  width: '100%',
  maxWidth: 480,
});

export function AuthCard({
  defaultReturnTo = '/',
  subtitle,
  allowedModes = ['login', 'register', 'recovery'],
  initialMode,
}: AuthCardProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeReturnTo(
    searchParams.get('returnTo'),
    defaultReturnTo
  );
  const handoff = readAuthHandoffParams(searchParams);
  const trustedRedirectUrl = isTrustedRedirectUrl(
    handoff.redirectUrl,
    TRUSTED_HANDOFF_ORIGINS
  )
    ? handoff.redirectUrl
    : null;
  const urlMode = searchParams.get('mode');
  const resolvedInitialMode = isKnownAuthMode(urlMode) ? urlMode : initialMode;
  const initialPhone =
    handoff.identityType === 'phone' ? (handoff.identity ?? undefined) : undefined;

  const user = useUserStore(userSelectors.user);
  const hydrated = useUserStore(userSelectors.hydrated);

  const goBack = useCallback(
    (context?: AuthOutcomeContext) => {
      if (trustedRedirectUrl) {
        window.location.href = appendAuthHandoffParams(trustedRedirectUrl, {
          identityType: 'phone',
          identity: context?.phone || null,
        });
        return;
      }

      navigate(returnTo, { replace: true });
    },
    [navigate, returnTo, trustedRedirectUrl]
  );

  useEffect(() => {
    if (hydrated && user) {
      goBack();
    }
  }, [hydrated, user, goBack]);

  return (
    <AuthCardFrame>
      <AuthSurface variant="outlined">
        <CardContent>
          <AuthFeature
            onCancel={goBack}
            onAuthenticated={goBack}
            subtitle={subtitle}
            allowedModes={allowedModes}
            initialMode={resolvedInitialMode}
            initialPhone={initialPhone}
          />
        </CardContent>
      </AuthSurface>
    </AuthCardFrame>
  );
}
